import { AfterViewInit, Component, ElementRef, OnInit, Renderer2, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthService } from 'src/services/auth.service';
import { UserProfile } from 'src/services/auth/userProfile';
import { JwtTokenService } from 'src/services/jwt-token.service';
import { LocalStorage } from 'src/utils/local-storage';
import { DatalayerService } from 'src/services/datalayer.service';
import { TokenTransferService } from 'src/services/token-transfer-service';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { CpsNavigationService } from 'src/services/cps-navigation.service';
import { environment } from 'src/environments/environment';


@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, AfterViewInit {
  @ViewChild('errorModal')
  errorModalTemplate: TemplateRef<void>;
  errorModalRef: BsModalRef;

  public loginErrorMessage$: BehaviorSubject<string>;

  public loginForm: FormGroup;
  public isButtonDisabled = false;
  public userIp$: Observable<string>;

  public userProfile: UserProfile = null;
  isMobile = false;
  isAuthenticated = true;

  constructor(private fb: FormBuilder,
    private renderer: Renderer2,
    private authService: AuthService,
    private localStorage: LocalStorage,
    private router: Router,
    private jwtTokenService: JwtTokenService,
    private tokenTransferService: TokenTransferService,
    private datalayer: DatalayerService,
    private modalService: BsModalService,
    private navigationService: CpsNavigationService) {
    this.loginErrorMessage$ = new BehaviorSubject<string>('');
    this.validate();
  }

  ngOnInit(): void {
    this.authService.isAuthenticated().subscribe(isAuth => {
      this.isAuthenticated = isAuth;
      if (isAuth) {
        this.router.navigate(['home']);
        return;
      }
      this.checkIfMobile();
      window.addEventListener('resize', () => this.checkIfMobile());

      //this.userIp$ = this.authService.getIpCheckMessage();

      this.loginPageViewEvent();

      const params = new Proxy(new URLSearchParams(window.location.search),
        { get: (searchParams, prop) => searchParams.get(prop as string), }
      );
      const pParam = (params as any).p;
      const action = (params as any).action;
      const isLogoutAction = !!action && action == 'logout';
      const isError = !!action && action == 'error';

      this.checkConnectivity();

      if (!this.isAuthenticated
          && document.referrer.indexOf('logout') < 0
          && document.referrer.indexOf('error') < 0
          && window.location.href.indexOf('logout') < 0
          && window.location.href.indexOf('error=') < 0
          && !isLogoutAction
          && !isError
          && window.self === window.top) {

      }

      if (pParam !== null) {
        this.authService.getAutoLoginByParam(pParam).subscribe({
          next: token => {
            // Successful login
            this.redirectSuccessfulLogin(token);
          },
          error: err => {
            this.loginErrorEvent(err, 'autologin');
          }
        });
      }

      if (typeof params.get === 'function') {

        const userid = params.get('userid');
        const password = params.get('password');

        if (userid && password) {
          // Populate the Angular login form
          this.loginForm.patchValue({
            username: userid,
            password: password
          });

            // Auto-submit
            setTimeout(() => this.login(), 200);
        }
      }

      // in case action=logout is in the param, remove it so the user can hit ctrl+r to log in
      if (isLogoutAction && !this.isAuthenticated) {
        window.history.pushState({}, document.title, "/");
      }


    });
  }

  ngAfterViewInit(): void {
    // Error modal can only be shown once the entire page renders
    // Check for error codes from the params from CPS, and show them in the modal
    const params = new Proxy(new URLSearchParams(window.location.search),
      { get: (searchParams, prop) => searchParams.get(prop as string), }
    );
    const error = (params as any).error;

    const action = (params as any).action;
    const errorCode = (params as any).errorCode;
    const isError = !!action && action == 'error';

    if (isError) {
      this.showModalForErrorCode(errorCode);
    } else if (!!error) {
      this.showModalForErrorCode(error);
    }

    const p = (params as any).p;

    this.router.navigate([], {
      queryParams: {p},
      replaceUrl: true
    });
  }

  async checkConnectivity(): Promise<void> {
    await this.authService.checkConnection().then(authVersion => {
        if (authVersion == null) {
          this.loginErrorMessage$.next('error.authentication.code.0');
          // Show error in a modal
          if (this.modalService.getModalsCount() == 0) {
            this.errorModalRef = this.modalService.show(this.errorModalTemplate);
          }
        } else {

          /* This block is to autologin users by IP, but it should not be done if:
            1. The user was previously logged out
            2. An error has occured
            3. This app is not the user's real browser (it's in an iframe)
            NOTE: This checks the referrer in case they are redirected to the login
          */

          this.authService.checkAutologinByIP().subscribe({
            next: token => {
              if (token.access_token != null) {
                this.localStorage.setAccessToken(token.access_token);
                this.localStorage.setRefreshToken(token.refresh_token);
                this.redirectSuccessfulLogin(token);
                this.router.navigate(['home']);
              } else {
                this.authService.setIpCheckMessage(token.ip);
              }
            },
            error: err => {
              this.loginErrorEvent(err, 'autologin');
            }
          });
        }
    });
  }

  validate(): void {
    this.loginForm = this.fb.group({
      username: new FormControl('', [Validators.required]),
      password: new FormControl('', [Validators.required]),
      rememberMe: [false]
    });
  }

login(): void {
  this.loginForm.markAllAsTouched();
  this.isButtonDisabled = true;

  if (this.loginForm.invalid) {
    this.isButtonDisabled = false;
    return;
  }

  const username = this.loginForm.controls.username.value;
  const password = this.loginForm.controls.password.value;

  this.authService.loginRequest(username, password, 'hub')
    .subscribe({
      next: token => {
        // Successful login
        this.redirectSuccessfulLogin(token);
      },
      error: err => {
        this.loginErrorEvent(err, username);
      }
    });
  }

  tabUser(): void {
    this.renderer.selectRootElement('#password').focus();
  }

  get loginFormControls() {
    return this.loginForm.controls;
  }

  redirectSuccessfulLogin(token: any) {
    this.userProfile = new UserProfile();

    this.jwtTokenService.setToken(token.access_token);
    this.userProfile = this.jwtTokenService.getUserInfo();
    this.userProfile.access_token = token.access_token;
    this.userProfile.refresh_token = token.refresh_token;
    const updatedAuthorities = this.jwtTokenService.getAuthorities();
    this.userProfile.authorities = updatedAuthorities;

    this.localStorage.setAccessToken(token.access_token);
    this.localStorage.setRefreshToken(token.refresh_token);
    this.localStorage.setUserProfile(this.userProfile);


    this.tokenTransferService.sendTokensToCps(this.userProfile);


    this.datalayer.loginSuccess();

    this.router.navigate(['home']);
  }

  loginErrorEvent(err: any, username: string) {
    // Failed login
    this.isButtonDisabled = false;
    this.loginErrorMessage$?.next(err.error.error_description);

    if (err.status === 504 || err.status === 502) {
      this.loginErrorMessage$.next('error.authentication.code.0');
      // Show error in a modal
      if (this.modalService.getModalsCount() == 0) {
        this.errorModalRef = this.modalService.show(this.errorModalTemplate);
      }
    } else {
      // Push login failure event to dataLayer
      this.datalayer.loginFailure(username);

      // Show error in a modal
      if (this.modalService.getModalsCount() == 0) {
        this.errorModalRef = this.modalService.show(this.errorModalTemplate);
      }
    }
  }

  showModalForErrorCode(errorCode: string) {
    this.isButtonDisabled = false;

    if (errorCode.indexOf('error.authentication.code') === -1) {
      this.loginErrorMessage$.next('error.authentication.code.' + errorCode);
    } else {
      this.loginErrorMessage$.next(errorCode);
    }

    if (this.modalService.getModalsCount() == 0) {
      this.errorModalRef = this.modalService.show(this.errorModalTemplate);
      this.navigationService.logout();
    }
  }

  closeModalForErrorCode(): void {
    // This is a safeguard in case two or more modals are open at once as that can lead to problems closing them.
    while (this.modalService.getModalsCount() > 0) {
      this.modalService.hide();
    }
  }

  private loginPageViewEvent(): void {
    this.datalayer.loginPageView();
  }

  checkIfMobile() {
    this.isMobile = window.innerWidth <= 768;
  }
}
